import { useCallback, useRef } from 'react';

import uniq from 'lodash/uniq';
import { di } from 'react-magnetic-di';
import { useRelayEnvironment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	type LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
	LOOKUP_SOURCE_USERS_ASYNC_DATA,
} from '../../../common/constants';
import type { FieldMapFromIframe } from '../../../common/types/field';
import type { LookupData, LookupUsers } from '../../../common/types/lookup';
import { isNotAutomaticId } from '../../../common/utils/is-not-automatic-id';
import { fetchUsers } from '../../../services/fetch-users';
import { useFormMetadata } from '../../issue-adjustments/hooks/use-form-metadata';
import { useViewType } from '../../issue-adjustments/hooks/use-view-type';
import { getSupportedFieldConfiguration } from '../../view-configuration';

import type { AsyncFieldsValues } from './types';

export const useLookupFieldData = () => {
	di(useRelayEnvironment, useFormMetadata, fetchUsers, useViewType);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const environment = useRelayEnvironment();

	const formMetadata = useFormMetadata();
	const formMetadataRef = useRef(formMetadata);
	const viewType = useViewType();

	/**
	 * We want to have an up-to-date data when this hook's
	 * returned function is being called in useSubmit callback function
	 * thats why we keep it in a ref object
	 */
	formMetadataRef.current = formMetadata;

	return useCallback(
		async (
			fieldsChanges: FieldMapFromIframe,
		): Promise<Exclude<LookupData, typeof LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY>> => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const lookupData = {} as Exclude<LookupData, typeof LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY>;

			const asyncFieldsValues = Object.entries(fieldsChanges).reduce(
				(acc: AsyncFieldsValues, [fieldId, fieldData]) => {
					if (!formMetadataRef.current[fieldId]) {
						return acc;
					}

					const { fieldType } = formMetadataRef.current[fieldId];
					const fieldValue = fieldData.value;
					const supportedFieldsConfiguration = getSupportedFieldConfiguration(viewType, fieldType);

					const lookupSource = supportedFieldsConfiguration?.lookupSource;
					const isUserLookupField = lookupSource === LOOKUP_SOURCE_USERS_ASYNC_DATA;

					if (isUserLookupField && fieldValue && isNotAutomaticId(fieldValue)) {
						const currentAccountIds = acc.accountIds ? [...acc.accountIds] : [];
						const newAccountIds = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
						acc.accountIds = [...currentAccountIds, ...newAccountIds];
					}
					/** Add extra checks to add other lookup type e.g. issues lookup */

					return acc;
				},
				{},
			);
			let users: LookupUsers = [];

			if (asyncFieldsValues.accountIds) {
				const uniqueAccountIds = uniq(asyncFieldsValues.accountIds);
				users = await fetchUsers(environment, uniqueAccountIds, createAnalyticsEvent);
			}

			lookupData[LOOKUP_SOURCE_USERS_ASYNC_DATA] = users;

			return lookupData;
		},
		[createAnalyticsEvent, viewType, environment],
	);
};
